import { AppStrings } from "../../../utils/constants/AppStrings";
import type { BonusTriggerEvent, CatalogEventField, EventCategoryGroup } from "./types";

/**
 * Built per call so TCM strings that arrive after boot (and language switches)
 * are picked up. Module-scope `AppStrings.X` snapshots freeze English defaults
 * before `loadAppStringsForLanguage` / `setAppString` rebind AppStrings.
 */
const eventCategoryLabels = (): Readonly<Record<string, string>> => ({
  Account: AppStrings.PAM_DYNAMIC_BONUS_EVENT_CATEGORY_ACCOUNT,
  Payments: AppStrings.PAM_DYNAMIC_BONUS_EVENT_CATEGORY_PAYMENTS,
  Engagement: AppStrings.PAM_DYNAMIC_BONUS_EVENT_CATEGORY_ENGAGEMENT,
  Sportsbook: AppStrings.PAM_DYNAMIC_BONUS_EVENT_CATEGORY_SPORTSBOOK,
  Casino: AppStrings.PAM_DYNAMIC_BONUS_EVENT_CATEGORY_CASINO,
  Lifecycle: AppStrings.PAM_DYNAMIC_BONUS_EVENT_CATEGORY_LIFECYCLE,
});

const eventDisplayNames = (): Readonly<Record<string, string>> => ({
  user_signup: AppStrings.PAM_DYNAMIC_BONUS_EVENT_USER_SIGNUP,
  first_time_deposit: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIRST_TIME_DEPOSIT,
  makes_purchase: AppStrings.PAM_DYNAMIC_BONUS_EVENT_MAKES_PURCHASE,
  user_withdrawal: AppStrings.PAM_DYNAMIC_BONUS_EVENT_USER_WITHDRAWAL,
  tax_paid: AppStrings.PAM_DYNAMIC_BONUS_EVENT_TAX_PAID,
  kyc_verified: AppStrings.PAM_DYNAMIC_BONUS_EVENT_KYC_VERIFIED,
  kyc_status_changed: AppStrings.PAM_DYNAMIC_BONUS_EVENT_KYC_STATUS_CHANGED,
  verified_email: AppStrings.PAM_DYNAMIC_BONUS_EVENT_VERIFIED_EMAIL,
  first_login_post_signup: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIRST_LOGIN_POST_SIGNUP,
  tenth_login: AppStrings.PAM_DYNAMIC_BONUS_EVENT_TENTH_LOGIN,
  daily_login_streak_hit: AppStrings.PAM_DYNAMIC_BONUS_EVENT_DAILY_LOGIN_STREAK_HIT,
  referred_friend: AppStrings.PAM_DYNAMIC_BONUS_EVENT_REFERRED_FRIEND,
  claims_referral_bonus: AppStrings.PAM_DYNAMIC_BONUS_EVENT_CLAIMS_REFERRAL_BONUS,
  followed_user: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FOLLOWED_USER,
  spin_used: AppStrings.PAM_DYNAMIC_BONUS_EVENT_SPIN_USED,
  game_pass_started: AppStrings.PAM_DYNAMIC_BONUS_EVENT_GAME_PASS_STARTED,
  campaign_start: AppStrings.PAM_DYNAMIC_BONUS_EVENT_CAMPAIGN_START,
  casino_bet_placed: AppStrings.PAM_DYNAMIC_BONUS_EVENT_CASINO_BET_PLACED,
  casino_bet_settled: AppStrings.PAM_DYNAMIC_BONUS_EVENT_CASINO_BET_SETTLED,
  casino_win: AppStrings.PAM_DYNAMIC_BONUS_EVENT_CASINO_WIN,
  bet_placed: AppStrings.PAM_DYNAMIC_BONUS_EVENT_BET_PLACED,
  placed_live_bet: AppStrings.PAM_DYNAMIC_BONUS_EVENT_PLACED_LIVE_BET,
  places_free_bet: AppStrings.PAM_DYNAMIC_BONUS_EVENT_PLACES_FREE_BET,
  places_bet_rebet_cash: AppStrings.PAM_DYNAMIC_BONUS_EVENT_PLACES_BET_REBET_CASH,
  places_profit_boost_bet: AppStrings.PAM_DYNAMIC_BONUS_EVENT_PLACES_PROFIT_BOOST_BET,
  failed_bet_insufficient_funds:
    AppStrings.PAM_DYNAMIC_BONUS_EVENT_FAILED_BET_INSUFFICIENT_FUNDS,
  parlay_loss_by_one: AppStrings.PAM_DYNAMIC_BONUS_EVENT_PARLAY_LOSS_BY_ONE,
  settled_cash_bet: AppStrings.PAM_DYNAMIC_BONUS_EVENT_SETTLED_CASH_BET,
  settled_sweat_bet: AppStrings.PAM_DYNAMIC_BONUS_EVENT_SETTLED_SWEAT_BET,
  settled_freebet: AppStrings.PAM_DYNAMIC_BONUS_EVENT_SETTLED_FREEBET,
  settled_profit_boost_bet: AppStrings.PAM_DYNAMIC_BONUS_EVENT_SETTLED_PROFIT_BOOST_BET,
});

const eventFieldLabels = (): Readonly<Record<string, string>> => ({
  bet_amount: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_BET_AMOUNT,
  payout: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_PAYOUT,
  bet_result: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_BET_RESULT,
  market_id: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_MARKET_ID,
  sport_id: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_SPORT_ID,
  "sr:tournament:id": AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_TOURNAMENT_ID,
  "sr:match:id": AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_MATCH_ID,
  league_name: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_LEAGUE_NAME,
  odds: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_ODDS,
  money_type: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_MONEY_TYPE,
  parlay_legs: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_PARLAY_LEGS,
  kyc_status: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_KYC_STATUS,
  amount: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_AMOUNT,
  currency: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_CURRENCY,
  user_id: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_USER_ID,
  bet_id: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_BET_ID,
});

const eventFieldTypeLabels = (): Readonly<Record<string, string>> => ({
  money: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_TYPE_MONEY,
  string: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_TYPE_STRING,
  number: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_TYPE_NUMBER,
  boolean: AppStrings.PAM_DYNAMIC_BONUS_EVENT_FIELD_TYPE_BOOLEAN,
});

export const eventCategoryDisplayLabel = (category: string): string =>
  eventCategoryLabels()[category] ?? category;

export const eventDisplayLabel = (eventKey: string, fallback: string): string =>
  eventDisplayNames()[eventKey] ?? fallback;

export const eventFieldDisplayLabel = (field: string, fallback: string): string =>
  eventFieldLabels()[field] ?? fallback;

export const eventFieldTypeDisplayLabel = (type: string): string =>
  eventFieldTypeLabels()[type] ?? type;

export const eventFieldOptionLabel = (
  field: string,
  fallbackLabel: string,
  type: string,
): string =>
  `${eventFieldDisplayLabel(field, fallbackLabel)} (${eventFieldTypeDisplayLabel(type)})`;

/** Schedule-injected catalog key — not a user/CRM event that can join a segment. */
const CAMPAIGN_START_EVENT_KEY = "campaign_start";

/** Returns every event from all groups as a flat list. */
export function flattenEvents(groups: EventCategoryGroup[]): BonusTriggerEvent[] {
  return groups.flatMap((g) => g.events);
}

/**
 * CRM sportsbook/payments events (e.g. `settled_cash_bet`) do fire as segment
 * triggers — `bonus_crm_events` dispatches to `evaluate_segments` (BONENG-915).
 * `campaign_start` is schedule-injected, so it is the only catalog key stripped
 * from the segment picker (same as the campaign wizard event Select).
 */
export function withoutCampaignStart(groups: EventCategoryGroup[]): EventCategoryGroup[] {
  return groups
    .map((group) => ({
      ...group,
      events: group.events.filter((e) => e.event_key !== CAMPAIGN_START_EVENT_KEY),
    }))
    .filter((group) => group.events.length > 0);
}

/** Produces Ant Design grouped Select options keyed by event_key. */
export function toGroupedSelectOptions(
  groups: EventCategoryGroup[],
): { label: string; options: { label: string; value: string }[] }[] {
  return groups.map((g) => ({
    label: eventCategoryDisplayLabel(g.category),
    options: g.events.map((e) => ({
      label: eventDisplayLabel(e.event_key, e.display_name),
      value: e.event_key,
    })),
  }));
}

/** Returns the display_name for a given event_key, or the key itself as fallback. */
export function displayNameFor(groups: EventCategoryGroup[], eventKey: string): string {
  for (const group of groups) {
    for (const event of group.events) {
      if (event.event_key === eventKey) {
        return eventDisplayLabel(event.event_key, event.display_name);
      }
    }
  }
  return eventKey;
}

/** Returns the catalog event for a given event_key, or undefined if not found. */
export function eventForKey(
  groups: EventCategoryGroup[],
  eventKey: string,
): BonusTriggerEvent | undefined {
  for (const group of groups) {
    for (const event of group.events) {
      if (event.event_key === eventKey) {
        return event;
      }
    }
  }
  return undefined;
}

/** Returns the filterable_fields for a given event_key, or [] if not found. */
export function fieldsForEvent(groups: EventCategoryGroup[], eventKey: string): CatalogEventField[] {
  return eventForKey(groups, eventKey)?.filterable_fields ?? [];
}
